import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, assetUrl, errText } from "../lib/api";
import { useBrand } from "../context/BrandContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import VisualEditor from "../components/VisualEditor";
import { PostDetailModal } from "../components/PostDetailModal";
import DenzelShareButton from "../components/DenzelShareButton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { DndContext, useDraggable, useDroppable, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../components/ui/hover-card";
import { Sparkles, Loader2, Check, XCircle, Link2, Lock, ImageIcon, Paintbrush, MessageSquare, MessageCircle, Film, AlertTriangle, Clock, Maximize2, Mic, Move, MousePointerClick, Pencil, RefreshCw, Zap, UserPlus, Users, Send, History, ExternalLink, LayoutTemplate } from "lucide-react";

const STATUS_STYLE = {
  pending_review: "bg-muted text-muted-foreground",
  approved: "bg-accent text-accent-foreground",
  changes_requested: "bg-destructive/15 text-destructive",
  scheduled: "bg-accent-2 text-accent-2-foreground",
  published: "bg-accent-2/80 text-accent-2-foreground",
};
const PLATFORM_LABEL = { instagram: "IG", linkedin: "LI", twitter: "X", facebook: "FB", tiktok: "TT" };

function PostCard({ post, onUpdate, bestTime, linkClicks, showGuides }) {
  const [copy, setCopy] = useState(post.copy);
  const [genBusy, setGenBusy] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const { attributes, listeners, setNodeRef: setDragRef, transform, isDragging } = useDraggable({ id: post.id });
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: post.id });

  // "generating" > 15 min without an update means the render died mid-way
  // (backend restart, ffmpeg crash, etc.). Treat it as not-busy so the
  // "Regenerate video" button unlocks instead of spinning forever, and
  // surface a one-line hint. Backend startup sweep now flips these to
  // `failed` too, but the guard here also covers races.
  const _startedAt = post.video_started_at ? new Date(post.video_started_at).getTime() : 0;
  const _staleGenerating = post.video_status === "generating"
    && _startedAt > 0
    && (Date.now() - _startedAt) > 15 * 60 * 1000;
  const vidBusy = (post.video_status === "generating" || post.video_status === "queued")
                  && !_staleGenerating;



  useEffect(() => {
    if (!vidBusy) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/posts/${post.id}`);
        if (data.video_status !== "generating" && data.video_status !== "queued") {
          onUpdate(data);
          if (data.video_status === "ready") toast.success("Video ready 🎬");
          else toast.error(data.video_error || "Video generation failed");
        }
      } catch {}
    }, 6000);
    return () => clearInterval(t);
  }, [vidBusy, post.id]);

  const genVideo = async (voice, engine = "slideshow") => {
    try {
      await api.post(`/posts/${post.id}/generate-video`, { voice, engine });
      onUpdate({ ...post, video_status: "generating" });
      toast.info(engine === "veo" ? "Rendering motion video with Veo AI (3–6 min)" : engine === "narrate" ? "Converting to a static image with voiceover & captions (~1 min)" : "Rendering video — script, voiceover & slideshow (1–2 min)");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const saveCopy = async () => {
    if (copy === post.copy) return;
    try {
      const { data } = await api.patch(`/posts/${post.id}`, { copy });
      onUpdate(data);
      toast.success("Copy saved");
    } catch (e) { /* interceptor shows the error */ }
  };

  const setStatus = async (review_status) => {
    try {
      const { data } = await api.patch(`/posts/${post.id}`, { review_status });
      onUpdate(data);
    } catch (e) { /* interceptor shows the error */ }
  };

  // The post is "effectively approved" if it's approved, scheduled to publish, or already published.
  // This means the Approve button stays green after Send Now / after the worker publishes.
  const isApproved = ["approved", "scheduled", "published"].includes(post.review_status);
  // "Sent" state persists (across refreshes) once the post has left the approval queue
  // and is on its way to Meta or already there.
  const isSent = ["scheduled", "published"].includes(post.review_status);

  const [unapproveOpen, setUnapproveOpen] = useState(false);
  const [unapproveBusy, setUnapproveBusy] = useState(false);
  const [deleteRemote, setDeleteRemote] = useState(true);
  const [ctaBusy, setCtaBusy] = useState(false);
  // Un-approving always requires an explicit confirmation dialog — the exact
  // copy + "also delete on Meta" checkbox varies by state (see the dialog).
  // Approving is instant (no confirmation, per user preference).
  const handleApproveToggle = () => {
    if (!isApproved) return setStatus("approved");
    return setUnapproveOpen(true);
  };
  const doUnapprove = async () => {
    setUnapproveBusy(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/unpublish`,
        { delete_remote: deleteRemote && post.review_status === "published" });
      onUpdate(data.post || { ...post, review_status: "pending_review" });
      // Green toast for full success; amber warning if Meta delete failed
      if (data.remote_error) {
        toast.warning(data.message || "Moved back to pending, but couldn't delete from social");
      } else {
        toast.success(data.message || "Post moved back to pending review");
      }
      setUnapproveOpen(false);
    } catch (e) { toast.error(errText(e)); }
    finally { setUnapproveBusy(false); }
  };

  const [sendingNow, setSendingNow] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const doSendNow = async () => {
    setSendingNow(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/publish-now`);
      onUpdate({ ...post, review_status: "scheduled",
                 scheduled_at: new Date().toISOString() });
      toast.success(data.message || "Publishing now — you'll see it live shortly");
      setConfirmOpen(false);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSendingNow(false);
    }
  };

  const [resendBusy, setResendBusy] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const doSendAgain = async () => {
    setResendBusy(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/send-again`);
      onUpdate({ ...post,
                 review_status: "scheduled",
                 scheduled_at: new Date().toISOString(),
                 external_post_id: null, permalink_url: null,
                 published_at: null, publish_mode: null });
      toast.success(data.message || "Re-sending — the post will re-publish within a minute");
      setResendOpen(false);
    } catch (e) { toast.error(errText(e)); }
    finally { setResendBusy(false); }
  };

  const genImage = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/generate-image`);
      // Backend returns the full post — includes cleared video_url, updated image_history & video_image_url
      onUpdate(data);
      toast.success("Visual generated");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setGenBusy(false);
    }
  };

  return (
    <Card ref={setDropRef} data-testid={`post-card-${post.id}`}
      style={transform ? { transform: `translate(${transform.x}px, ${transform.y}px)`, zIndex: 50, position: "relative" } : undefined}
      className={`overflow-hidden flex flex-col transition-shadow ${isOver && !isDragging ? "ring-2 ring-primary shadow-lg" : ""} ${isDragging ? "shadow-2xl opacity-90" : ""}`}>
      {/* Full-bleed 9:16 reel-shaped media — the visual is the hero */}
      <div className="relative aspect-[9/16] bg-black flex items-center justify-center">
        {post.video_url ? (
          <video src={assetUrl(post.video_url)} poster={post.video_poster_url ? assetUrl(post.video_poster_url) : (post.video_image_url ? assetUrl(post.video_image_url) : (post.image_url ? assetUrl(post.image_url) : undefined))} controls playsInline preload="metadata" className="w-full h-full object-cover bg-black" data-testid={`post-video-${post.id}`} />
        ) : post.image_url ? (
          <img src={assetUrl(post.image_url)} alt="" draggable={false} className="w-full h-full object-cover cursor-zoom-in" data-testid={`post-media-${post.id}`} onClick={() => setDetailOpen(true)} />
        ) : (
          <div className="text-center text-white/60 p-6 cursor-pointer" data-testid={`post-media-${post.id}`} onClick={() => setDetailOpen(true)}>
            <ImageIcon size={28} className="mx-auto mb-2 opacity-50" />
            <p className="text-xs">No visual yet</p>
          </div>
        )}
        {/* Minimal top overlay: platform pill + status only — no clutter over the reel */}
        <div className="absolute top-2 left-2 flex gap-1.5">
          <Badge className="bg-black/60 backdrop-blur-sm text-white border-0 text-[10px] px-1.5 py-0.5">{PLATFORM_LABEL[post.platform] || post.platform}</Badge>
          {post.video_url && <Badge className="bg-black/60 backdrop-blur-sm text-white border-0 text-[10px] px-1.5 py-0.5" data-testid={`video-badge-${post.id}`}><Film size={9} className="mr-0.5" /> Reel</Badge>}
        </div>
        <Badge className={`absolute top-2 right-2 border-0 capitalize text-[10px] px-1.5 py-0.5 ${STATUS_STYLE[post.review_status]}`} data-testid={`post-status-${post.id}`}>
          {post.review_status.replace("_", " ")}
        </Badge>
        {/* MOTION / Veo fallback badges at bottom-left over the reel, subtle */}
        <div className="absolute bottom-2 left-2 flex gap-1.5">
          {post.video_engine === "veo" && (
            <Badge className="bg-gradient-to-r from-fuchsia-500 to-amber-400 text-black border-0 text-[10px] px-1.5 py-0.5 font-bold" title="Full-motion Veo animation" data-testid={`motion-badge-${post.id}`}>
              <Zap size={9} className="mr-0.5" /> MOTION
            </Badge>
          )}
          {post.video_fallback_note && (
            <Badge className="bg-amber-500/90 text-black border-0 text-[10px] px-1.5 py-0.5" title={post.video_fallback_note} data-testid={`veo-fallback-badge-${post.id}`}>
              <AlertTriangle size={9} className="mr-0.5" /> Veo fallback
            </Badge>
          )}
        </div>
        {showGuides && (
          <div className="absolute inset-0 pointer-events-none" data-testid={`safe-zone-guides-${post.id}`}>
            {/* Right action-button rail (~12% width) */}
            <div className="absolute top-0 bottom-0 right-0 bg-red-500/15 border-l border-red-400/50" style={{ width: "12%" }}>
              <span className="absolute top-1/2 -translate-y-1/2 -rotate-90 whitespace-nowrap text-[7px] font-semibold text-red-200 left-1/2 -translate-x-1/2">BUTTONS</span>
            </div>
            {/* Bottom caption / CTA band (~16% height) */}
            <div className="absolute left-0 right-0 bottom-0 bg-amber-400/15 border-t border-amber-300/50" style={{ height: "16%" }}>
              <span className="absolute top-1 left-1 text-[7px] font-semibold text-amber-100">CAPTION / CTA</span>
            </div>
            {/* Top status band (~7%) */}
            <div className="absolute left-0 right-0 top-0 bg-sky-400/10 border-b border-sky-300/40" style={{ height: "7%" }} />
            {/* Watermark landing box (300px ≈ 28% wide, right 120px ≈ 11%, bottom 260px ≈ 13.5%) */}
            <div className="absolute border border-cyan-300/80 border-dashed rounded" style={{ width: "28%", height: "3.5%", right: "11%", bottom: "13.5%" }}>
              <span className="absolute -top-3 right-0 text-[7px] font-semibold text-cyan-200">watermark</span>
            </div>
          </div>
        )}
      </div>
      {/* Below the reel: prominent Drag + Edit row (restored to the earlier design), then caption + meta + actions */}
      <div className="flex gap-2 px-3 pt-2.5">
        <Button ref={setDragRef} {...listeners} {...attributes} size="sm" title="Drag onto another post to swap timeslots"
          data-testid={`drag-handle-${post.id}`}
          className="h-9 flex-1 bg-primary text-primary-foreground hover:bg-primary/85 cursor-grab active:cursor-grabbing touch-none">
          <Move size={16} />
        </Button>
        <Button size="sm" className="h-9 flex-[3] bg-primary text-primary-foreground hover:bg-primary/85" title="Open editor"
          data-testid={`edit-post-btn-${post.id}`} onClick={() => setDetailOpen(true)}>
          <Pencil size={15} className="mr-1.5" /> Edit
        </Button>
      </div>
      <CardContent className="p-3 space-y-2 flex-1 flex flex-col">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <button type="button" className="font-semibold text-sm hover:underline flex items-center gap-1 min-w-0" data-testid={`open-detail-btn-${post.id}`} onClick={() => setDetailOpen(true)}>
            <Maximize2 size={11} className="opacity-60 shrink-0" /> <span className="truncate">{post.title}</span>
          </button>
          <div className="flex gap-1">
            <Badge variant="secondary" className="capitalize text-[10px] px-1.5 py-0.5">{post.pillar}</Badge>
            {post.brand && <Badge className="bg-accent text-accent-foreground border-0 text-[10px] px-1.5 py-0.5">{post.brand}</Badge>}
          </div>
        </div>
        {bestTime?.best?.length > 0 && (
          <p className="text-[10px] text-muted-foreground flex items-center gap-1" data-testid={`best-time-hint-${post.id}`}
             title={bestTime.source === "followers" ? "Based on when your followers engage most" : "Platform benchmark — not enough follower data yet"}>
            <Clock size={10} /> Best time: {bestTime.best[0].label}{bestTime.source === "benchmark" ? " · benchmark" : ""}
          </p>
        )}
        <p className="text-xs whitespace-pre-line line-clamp-2 flex-1 cursor-pointer text-muted-foreground" data-testid={`post-copy-preview-${post.id}`} onClick={() => setDetailOpen(true)} title="Open editor to see the full caption">{copy}</p>
        {post.hashtags?.length > 0 && <p className="text-[11px] text-accent leading-relaxed line-clamp-1">{post.hashtags.map((h) => `#${h}`).join(" ")}</p>}
        {post.first_comment && (
          <div className="text-[11px] p-2 rounded bg-muted text-muted-foreground line-clamp-2"><span className="font-semibold text-foreground">1st comment:</span> {post.first_comment}</div>
        )}
        {post.comments?.length > 0 && (
          <div className="text-[11px] flex items-center gap-1 text-muted-foreground"><MessageSquare size={12} /> {post.comments.length} client comment(s): <span className="italic truncate">"{post.comments[post.comments.length - 1].text}"</span></div>
        )}
        <p className="text-[11px] text-muted-foreground flex items-center gap-1 pt-1" data-testid={`post-schedule-${post.id}`}>
          <Clock size={11} /> {new Date(post.scheduled_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · {new Date(post.scheduled_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
          {post.track_url && (
            <span className="ml-auto flex items-center gap-1 text-accent font-semibold" title={`Clicks on ${post.track_url}`} data-testid={`link-clicks-${post.id}`}>
              <MousePointerClick size={11} /> {linkClicks || 0} clicks
            </span>
          )}
        </p>
        <div className="flex gap-2">
          <Button size="sm" className={`flex-1 ${isApproved ? "bg-green-600 hover:bg-green-500 text-white border-green-600" : ""}`}
            data-testid={`approve-post-btn-${post.id}`} variant={isApproved ? "default" : "outline"}
            title={isSent ? "Tap to unapprove — you'll be asked whether to also delete from social" :
                   isApproved ? "Tap to move back to Pending review" : "Approve this post"}
            onClick={handleApproveToggle}>
            <Check size={13} className="mr-1" /> {isApproved ? "Approved · tap to undo" : "Approve"}
          </Button>
          {isApproved && (
            <Button size="sm"
              variant={isSent ? "default" : "outline"}
              className={`flex-1 ${isSent ? "bg-green-600 hover:bg-green-600 text-white border-green-600" : ""}`}
              data-testid={`send-now-btn-${post.id}`}
              disabled={sendingNow || isSent}
              title={isSent
                ? `Sent to ${(post.platform || "").replace(/^./, c => c.toUpperCase())} · ${new Date(post.published_at || post.scheduled_at || Date.now()).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}${post.permalink_url ? "  · click through in Analytics" : ""}`
                : "Publish this approved post immediately (bypass the schedule)"}
              onClick={() => setConfirmOpen(true)}>
              {isSent ? <Check size={13} className="mr-1" /> :
               sendingNow ? <Loader2 size={13} className="mr-1 animate-spin" /> :
               <Send size={13} className="mr-1" />}
              {isSent ? "Sent" : sendingNow ? "Sending…" : "Send now"}
            </Button>
          )}
          {post.review_status === "pending_review" && (
            <Button size="sm" variant="outline" className="flex-1 text-destructive" data-testid={`reject-post-btn-${post.id}`} onClick={() => setStatus("changes_requested")}>
              <XCircle size={13} className="mr-1" /> Changes
            </Button>
          )}
        </div>
        {post.review_status === "published" && (
          <div className="pt-2 border-t border-dashed space-y-2">
            {post.permalink_url && ["facebook", "instagram"].includes(post.platform) && post.first_comment
             && !post.first_comment_id && !post.remote_deleted && (
              <button type="button"
                data-testid={`post-first-comment-chip-${post.id}`}
                className="text-[11px] w-full text-left rounded-md border border-amber-500/60 bg-amber-50/50 hover:bg-amber-100 text-amber-900 px-2 py-1 flex items-center gap-1.5 transition-colors disabled:opacity-60"
                disabled={ctaBusy}
                title={post.first_comment_error ? `Meta rejected: ${post.first_comment_error}` : "The CTA comment didn't post on publish — retry now"}
                onClick={async () => {
                  setCtaBusy(true);
                  try {
                    const { data } = await api.post(`/posts/${post.id}/post-first-comment`, {});
                    if (data.post) onUpdate(data.post);
                    toast.success("CTA comment posted to " + post.platform);
                  } catch (e) { toast.error(errText(e)); }
                  finally { setCtaBusy(false); }
                }}>
                {ctaBusy ? <Loader2 size={11} className="animate-spin" /> : <MessageCircle size={11} />}
                {post.first_comment_error ? "Meta rejected the CTA — retry" : "Post CTA comment now"}
              </button>
            )}
            {post.permalink_url && (
              <DenzelShareButton post={post} onShared={(d) => onUpdate({ ...post, denzel_share_status: "shared" })} />
            )}
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="flex-1 text-[11px]"
                data-testid={`send-again-btn-${post.id}`}
                disabled={resendBusy}
                title={`Re-publish this post — the previous send is kept in publish history`}
                onClick={() => setResendOpen(true)}>
                {resendBusy ? <Loader2 size={11} className="mr-1 animate-spin" /> : <Send size={11} className="mr-1" />}
                Send again
              </Button>
              {(() => {
                const history = post.publish_history || [];
                const sendCount = history.length + 1;
                if (sendCount < 2) return null;
                // Newest first — the current live send is `post` itself, and
                // history holds every prior attempt in insertion order.
                const timeline = [
                  { external_post_id: post.external_post_id,
                    permalink_url: post.permalink_url,
                    published_at: post.published_at,
                    routed_channels: post.routed_channels,
                    current: true },
                  ...[...history].reverse(),
                ];
                return (
                  <HoverCard openDelay={100} closeDelay={200}>
                    <HoverCardTrigger asChild>
                      <button type="button"
                        data-testid={`send-again-history-chip-${post.id}`}
                        className="text-[10px] rounded-full border border-primary/40 bg-primary/10 hover:bg-primary/20 text-primary px-2 py-1 flex items-center gap-1 transition-colors cursor-help">
                        <History size={10} /> Sent {sendCount}×
                      </button>
                    </HoverCardTrigger>
                    <HoverCardContent align="end" className="w-80 p-0"
                      data-testid={`send-again-history-popover-${post.id}`}>
                      <div className="px-4 py-3 border-b bg-muted/40">
                        <p className="text-xs font-semibold flex items-center gap-1.5">
                          <History size={12} /> Publish history · {sendCount} send{sendCount !== 1 ? "s" : ""}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">Every re-send keeps the original live and appends here.</p>
                      </div>
                      <ul className="max-h-64 overflow-y-auto divide-y">
                        {timeline.map((entry, i) => {
                          const when = entry.published_at ? new Date(entry.published_at) : null;
                          const label = entry.current ? "Current live send" : `Send #${sendCount - i}`;
                          return (
                            <li key={`${entry.external_post_id || i}-${i}`} className="px-4 py-2.5 text-[11px] flex items-start gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold">{label}</span>
                                  {entry.current && <Badge variant="secondary" className="text-[9px] px-1.5 py-0">Live</Badge>}
                                </div>
                                {when && (
                                  <div className="text-muted-foreground mt-0.5" data-testid={`send-again-history-when-${post.id}-${i}`}>
                                    {when.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
                                  </div>
                                )}
                                {entry.external_post_id && (
                                  <div className="text-muted-foreground font-mono text-[10px] mt-0.5 truncate">
                                    id · {entry.external_post_id}
                                  </div>
                                )}
                              </div>
                              {entry.permalink_url && (
                                <a href={entry.permalink_url} target="_blank" rel="noreferrer"
                                  className="text-primary hover:text-primary/80 flex items-center gap-0.5 text-[10px] shrink-0"
                                  data-testid={`send-again-history-link-${post.id}-${i}`}>
                                  Open <ExternalLink size={9} />
                                </a>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </HoverCardContent>
                  </HoverCard>
                );
              })()}
            </div>
          </div>
        )}
      </CardContent>
      <Dialog open={confirmOpen} onOpenChange={(o) => !sendingNow && setConfirmOpen(o)}>
        <DialogContent className="sm:max-w-md !bg-[#fbf0eb] !text-[#1d3d47] border-2 border-[#c69d56] shadow-2xl" data-testid={`send-now-dialog-${post.id}`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Send size={18} /> Publish now?
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-2 !text-[#1d3d47]/85">
              <span className="block text-foreground font-medium">"{post.title}"</span>
              <span className="block text-xs !text-[#1d3d47]/75">
                Going live on <b className="capitalize">{post.platform}</b> within a minute.
              </span>
              {post.scheduled_at && (
                <span className="block text-xs !text-[#1d3d47]/75">
                  Overrides the currently scheduled time
                  {" "}({new Date(post.scheduled_at).toLocaleString(undefined, { month:"short", day:"numeric", hour:"numeric", minute:"2-digit" })}).
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={sendingNow}
                    data-testid={`send-now-cancel-${post.id}`}>Cancel</Button>
            <Button onClick={doSendNow} disabled={sendingNow}
                    data-testid={`send-now-confirm-${post.id}`}>
              {sendingNow ? <><Loader2 size={13} className="mr-1 animate-spin" /> Sending…</> : <><Send size={13} className="mr-1" /> Send now</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resendOpen} onOpenChange={(o) => !resendBusy && setResendOpen(o)}>
        <DialogContent className="sm:max-w-md !bg-[#fbf0eb] !text-[#1d3d47] border-2 border-[#c69d56] shadow-2xl" data-testid={`send-again-dialog-${post.id}`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Send size={18} /> Re-send this post?
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-2 !text-[#1d3d47]/85">
              <span className="block text-foreground font-medium">"{post.title}"</span>
              <span className="block text-xs !text-[#1d3d47]/75">
                A fresh copy will publish to <b className="capitalize">{post.platform}</b> within a minute — the original stays live too. This creates a second post on your channel.
              </span>
              {post.published_at && (
                <span className="block text-xs !text-[#1d3d47]/75">
                  Original sent: <b>{new Date(post.published_at).toLocaleString(undefined, { month:"short", day:"numeric", hour:"numeric", minute:"2-digit" })}</b>.
                  {post.publish_history?.length ? ` This will be send #${post.publish_history.length + 2}.` : " This will be send #2."}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setResendOpen(false)} disabled={resendBusy}
                    data-testid={`send-again-cancel-${post.id}`}>Cancel</Button>
            <Button onClick={doSendAgain} disabled={resendBusy}
                    data-testid={`send-again-confirm-${post.id}`}>
              {resendBusy ? <><Loader2 size={13} className="mr-1 animate-spin" /> Sending…</> : <><Send size={13} className="mr-1" /> Yes, send again</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={unapproveOpen} onOpenChange={(o) => !unapproveBusy && setUnapproveOpen(o)}>
        <DialogContent className="sm:max-w-md !bg-[#fbf0eb] !text-[#1d3d47] border-2 border-[#c69d56] shadow-2xl" data-testid={`unapprove-dialog-${post.id}`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <XCircle size={18} /> Un-approve this post?
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-2 !text-[#1d3d47]/85">
              <span className="block text-foreground font-medium">"{post.title}"</span>
              <span className="block text-xs !text-[#1d3d47]/75">
                {post.review_status === "published"
                  ? <>This post is already live on <b className="capitalize">{post.platform}</b>. Moving it back to Pending review keeps the local record editable.</>
                  : post.review_status === "scheduled"
                    ? <>This post is queued for <b className="capitalize">{post.platform}</b>. Un-approving cancels the queued publish and moves it back to Pending review.</>
                    : <>This post is approved and waiting for its scheduled window. Un-approving moves it back to Pending review so you can edit it further.</>}
              </span>
            </DialogDescription>
          </DialogHeader>
          {post.review_status === "published" && (
            <label className="flex items-start gap-2 text-xs cursor-pointer rounded-md border p-3 bg-muted/40"
                   data-testid={`unapprove-delete-remote-${post.id}`}>
              <input type="checkbox" checked={deleteRemote}
                     onChange={(e) => setDeleteRemote(e.target.checked)}
                     className="mt-0.5" />
              <span>
                <b>Also delete the post from {post.platform}</b>
                <span className="block !text-[#1d3d47]/70 mt-0.5">
                  Removes it from your brand's feed via the Meta Graph API. Uncheck to keep it live even though it's unapproved here.
                </span>
              </span>
            </label>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setUnapproveOpen(false)} disabled={unapproveBusy}
                    data-testid={`unapprove-cancel-${post.id}`}>Cancel</Button>
            <Button variant="destructive" onClick={doUnapprove} disabled={unapproveBusy}
                    data-testid={`unapprove-confirm-${post.id}`}>
              {unapproveBusy ? <><Loader2 size={13} className="mr-1 animate-spin" /> Working…</> :
               <><XCircle size={13} className="mr-1" /> {post.review_status === "published" && deleteRemote ? "Un-approve & delete" : "Un-approve"}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {editorOpen && <VisualEditor post={post} open={editorOpen} onClose={() => setEditorOpen(false)} onSaved={(url) => { onUpdate({ ...post, image_url: url }); setEditorOpen(false); }} />}
      {detailOpen && (
        <PostDetailModal post={post} open={detailOpen} onClose={() => setDetailOpen(false)} onUpdate={onUpdate} bestTime={bestTime}
          copy={copy} setCopy={setCopy} saveCopy={saveCopy} genImage={genImage} genBusy={genBusy} genVideo={genVideo} vidBusy={vidBusy}
          onOpenEditor={() => setEditorOpen(true)} />
      )}
    </Card>
  );
}

export default function ReviewFeed() {
  const [params, setParams] = useSearchParams();
  const { selected } = useBrand();
  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState(params.get("batch") || "");
  const [posts, setPosts] = useState([]);
  const [batch, setBatch] = useState(null);
  const [bestTimes, setBestTimes] = useState({});
  const [clicks, setClicks] = useState({});

  useEffect(() => {
    api.get("/analytics/best-times", { params: selected ? { brand: selected } : {} }).then((r) => setBestTimes(r.data)).catch(() => {});
  }, [selected]);

  useEffect(() => {
    api.get("/batches", { params: selected ? { brand: selected } : {} }).then((r) => {
      setBatches(r.data);
      if (!r.data.some((b) => b.id === batchId)) setBatchId(r.data.length ? r.data[0].id : "");
    });
  }, [selected]);

  const loadBatch = useCallback(() => {
    if (!batchId) { setBatch(null); setPosts([]); return; }
    api.get(`/analytics/link-clicks`, { params: { batch_id: batchId } }).then((r) => setClicks(r.data)).catch(() => {});
    api.get(`/batches/${batchId}`).then((r) => {
      setBatch(r.data.batch);
      setPosts(selected ? r.data.posts.filter((p) => !p.brand || p.brand === selected) : r.data.posts);
    });
  }, [batchId, selected]);
  useEffect(loadBatch, [loadBatch]);

  const onUpdate = (p) => setPosts((prev) => prev.map((x) => (x.id === p.id ? { ...x, ...p } : x)));
  const approvedCount = posts.filter((p) => ["approved", "scheduled"].includes(p.review_status)).length;

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/approve/${batch.approval_token}`);
    toast.success("Client approval link copied");
  };

  const approveAll = async () => {
    await api.post(`/batches/${batchId}/approve-all`);
    loadBatch();
    toast.success("All approved posts promoted to the publishing queue 🔒");
  };

  const bulkApprove = async () => {
    const pendingCount = (posts || []).filter((p) => p.review_status === "pending_review").length;
    if (!pendingCount) { toast.info("No pending posts to approve"); return; }
    if (!window.confirm(`Approve all ${pendingCount} pending posts in this batch?\n\nThis moves them from "Pending review" to "Approved" but does NOT lock the batch or schedule them for publishing.`)) return;
    try {
      const { data } = await api.post(`/batches/${batchId}/bulk-approve`);
      toast.success(`${data.approved} posts approved ✓`);
      loadBatch();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } })
  );

  const swapSlots = async (fromId, toId) => {
    try {
      await api.post(`/posts/${fromId}/swap-schedule`, { other_id: toId });
      loadBatch();
      toast.success("Timeslots swapped — both posts now hold each other's date & time");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const narrateAll = async (voice) => {
    try {
      const { data } = await api.post(`/batches/${batchId}/narrate-all`, { voice });
      if (!data.queued) { toast.info("Every image post already has a video"); return; }
      toast.success(`Narrating ${data.queued} posts — videos will appear one by one (~1 min each)`);
      loadBatch();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const [refreshingText, setRefreshingText] = useState(false);
  const [bulkVoiceBusy, setBulkVoiceBusy] = useState(false);
  const [includeApproved, setIncludeApproved] = useState(false);
  const [motionFilter, setMotionFilter] = useState("all"); // all | motion | slideshow
  const [showGuides, setShowGuides] = useState(false);

  const VOICE_LABEL = { british: "British female", warm: "Warm female", energetic: "Energetic male", adriana: "Adriana", alberto: "Alberto", aria: "Aria", amelia: "Amelia" };

  const bulkReassignVoice = async (voice, visible) => {
    if (!visible.length) return;
    setBulkVoiceBusy(true);
    const wait = toast.loading(`Reassigning ${visible.length} post${visible.length === 1 ? "" : "s"} to ${VOICE_LABEL[voice]}…`);
    try {
      await Promise.all(visible.map((p) => api.patch(`/posts/${p.id}`, { video_voice: voice })));
      toast.success(`Voice set to ${VOICE_LABEL[voice]} on ${visible.length} post${visible.length === 1 ? "" : "s"}`, {
        id: wait,
        duration: 12000,
        action: {
          label: `Re-render all with ${VOICE_LABEL[voice]}`,
          onClick: async () => {
            const w2 = toast.loading(`Queuing ${visible.length} narrations with ${VOICE_LABEL[voice]}…`);
            try {
              // Queue every visible post that already has an image; server re-renders sequentially
              await Promise.all(visible.filter((p) => p.image_url).map((p) => api.patch(`/posts/${p.id}`, { video_status: "queued" })));
              const { data } = await api.post(`/batches/${batchId}/narrate-all`, { voice });
              toast.success(data.queued ? `Re-rendering ${data.queued} videos with ${VOICE_LABEL[voice]}` : "No image posts to narrate", { id: w2 });
              loadBatch();
            } catch (e) { toast.error(errText(e), { id: w2 }); }
          },
        },
      });
      loadBatch();
    } catch (e) {
      toast.error(errText(e), { id: wait });
    } finally {
      setBulkVoiceBusy(false);
    }
  };

  const [coachDialogOpen, setCoachDialogOpen] = useState(false);
  const [coachForm, setCoachForm] = useState({ coach: "alberto", topic: "", platform: "facebook" });
  const [coachBusy, setCoachBusy] = useState(false);
  const [companions, setCompanions] = useState([]);
  const BRAND_HAS_DUET = { Roujaune: true };
  const brandForDuet = batch?.brand;
  const canDuet = !!BRAND_HAS_DUET[brandForDuet];
  const [duetDialogOpen, setDuetDialogOpen] = useState(false);
  const [duetForm, setDuetForm] = useState({ topic: "", platform: "facebook" });
  const [duetBusy, setDuetBusy] = useState(false);

  // Fetch companion registry for the current batch's brand — falls back to all companions
  // when the batch has no brand set.
  useEffect(() => {
    if (!batch) return;
    const params = batch.brand ? { brand: batch.brand } : {};
    api.get("/companions", { params }).then((r) => {
      const list = r.data?.companions || [];
      setCompanions(list);
      // Prefer a companion that belongs to this brand; otherwise keep current selection
      if (list.length && !list.find((c) => c.key === coachForm.coach)) {
        setCoachForm((f) => ({ ...f, coach: list[0].key }));
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch?.brand]);

  const activeCompanion = companions.find((c) => c.key === coachForm.coach) || companions[0];

  // Brand-specific quick topics for the coach post picker
  const COACH_TOPIC_PRESETS = {
    Roujaune: ["Cadence work for climbing", "Recovery week structure", "VO2 max intervals", "Base miles in winter", "Race day fuelling", "Sprint form", "Zone 2 endurance"],
    "Fit Beyond 50": ["Balance & fall prevention", "Chair-based strength", "Joyful walking habit", "Protein for 50+", "Sleep for recovery", "Mobility warm-up", "Grip strength daily"],
    "The Peaceful Pause": ["Box breathing at bedtime", "Morning stillness ritual", "Digital sunset", "Gentle body scan", "Journalling three sentences", "Sunlight & mood", "5-minute reset walk"],
  };
  const topicPresets = COACH_TOPIC_PRESETS[batch?.brand] || COACH_TOPIC_PRESETS.Roujaune;

  const createCoachPost = async () => {
    if (!coachForm.topic.trim()) { toast.error("Topic is required"); return; }
    setCoachBusy(true);
    const name = activeCompanion?.name || coachForm.coach;
    const wait = toast.loading(`Generating post with ${name}…`);
    try {
      const { data } = await api.post(`/batches/${batchId}/coach-post`, coachForm);
      toast.success(`Coach post added — "${data.title}" (media rendering in the background)`, { id: wait });
      setCoachDialogOpen(false);
      setCoachForm({ ...coachForm, topic: "" });
      loadBatch();
    } catch (e) {
      toast.error(errText(e), { id: wait });
    } finally {
      setCoachBusy(false);
    }
  };

  const createDuetPost = async () => {
    if (!duetForm.topic.trim()) { toast.error("Topic is required"); return; }
    setDuetBusy(true);
    const wait = toast.loading(`Generating Adriana × Alberto duet on "${duetForm.topic}"…`);
    try {
      const { data } = await api.post(`/batches/${batchId}/duet-post`, duetForm);
      toast.success(`Duet post added — "${data.title}" (dialogue video rendering in the background, ~3-5 min)`, { id: wait });
      setDuetDialogOpen(false);
      setDuetForm({ ...duetForm, topic: "" });
      loadBatch();
    } catch (e) {
      toast.error(errText(e), { id: wait });
    } finally {
      setDuetBusy(false);
    }
  };
  const refreshableBase = includeApproved
    ? posts
    : posts.filter((p) => p.review_status !== "approved" && p.review_status !== "scheduled" && p.review_status !== "published");
  const refreshable = refreshableBase;
  const refreshableCount = refreshable.length;
  const availablePillars = Array.from(new Set(refreshable.map((p) => p.pillar).filter(Boolean))).sort();
  const runRefresh = async (pillars = null) => {
    const scope = pillars ? refreshable.filter((p) => pillars.includes(p.pillar)) : refreshable;
    if (!scope.length) { toast.info(includeApproved ? "Nothing to refresh in that pillar" : "Nothing to refresh in that pillar — tick 'Include approved' to rewrite locked posts"); return; }
    setRefreshingText(true);
    const wait = toast.loading(`Rewriting ${scope.length} post${scope.length === 1 ? "" : "s"}${pillars ? ` in ${pillars.join(", ")}` : ""}${includeApproved ? " (incl. approved)" : ""}…`);
    try {
      const body = { include_approved: includeApproved };
      if (pillars) body.pillars = pillars;
      const { data } = await api.post(`/batches/${batchId}/refresh-text-all`, body);
      toast.success(`Refreshed ${data.refreshed} of ${data.total} — all back in Pending review`, { id: wait });
      loadBatch();
    } catch (e) {
      toast.error(errText(e), { id: wait });
    } finally {
      setRefreshingText(false);
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="review-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-1">Review Feed{selected ? ` · ${selected}` : ""}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold">Polish before it ships.</h1>
        </div>
        <Select value={batchId} onValueChange={(v) => { setBatchId(v); setParams({ batch: v }); }}>
          <SelectTrigger className="w-64" data-testid="batch-select"><SelectValue placeholder="Select a batch" /></SelectTrigger>
          <SelectContent>
            {batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} ({b.status})</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {batch && posts.length > 0 && (
        <Card>
          <CardContent className="p-5 flex flex-wrap items-center gap-5">
            <div className="flex-1 min-w-[220px]">
              <p className="text-sm font-semibold mb-2" data-testid="approval-progress-text">{approvedCount} of {posts.length} posts approved</p>
              <Progress value={(approvedCount / posts.length) * 100} className="h-2" />
            </div>
            <div className="flex gap-3 flex-wrap">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="refresh-text-all-btn" disabled={refreshingText || !refreshableCount} title={refreshableCount ? "Rewrite non-approved posts (all or by pillar)" : "All posts approved"}>
                    {refreshingText ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />} Refresh text{refreshableCount ? ` (${refreshableCount})` : ""}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel className="text-xs flex items-center justify-between gap-3">
                    <span>Rewrite copy</span>
                    <label className="text-[10px] font-normal cursor-pointer flex items-center gap-1.5 select-none" data-testid="refresh-include-approved-label">
                      <input type="checkbox" checked={includeApproved} onChange={(e) => { e.stopPropagation(); setIncludeApproved(e.target.checked); }} onClick={(e) => e.stopPropagation()} className="h-3.5 w-3.5 accent-accent" data-testid="refresh-include-approved-toggle" />
                      Include approved
                    </label>
                  </DropdownMenuLabel>
                  <DropdownMenuItem data-testid="refresh-text-all-scope" onClick={() => runRefresh(null)}>{includeApproved ? `Every post (${refreshableCount})` : `All non-approved (${refreshableCount})`}</DropdownMenuItem>
                  {availablePillars.length > 0 && <DropdownMenuSeparator />}
                  {availablePillars.map((pl) => {
                    const n = refreshable.filter((p) => p.pillar === pl).length;
                    return <DropdownMenuItem key={pl} data-testid={`refresh-text-pillar-${pl}`} onClick={() => runRefresh([pl])}>Only "{pl}" ({n})</DropdownMenuItem>;
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="narrate-all-btn"><Mic size={14} className="mr-2" /> Narrate all</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel className="text-xs">Voice a video for every image post</DropdownMenuLabel>
                  <DropdownMenuItem data-testid="narrate-all-british" onClick={() => narrateAll("british")}>British female voice</DropdownMenuItem>
                  <DropdownMenuItem data-testid="narrate-all-warm" onClick={() => narrateAll("warm")}>Warm female voice</DropdownMenuItem>
                  <DropdownMenuItem data-testid="narrate-all-energetic" onClick={() => narrateAll("energetic")}>Energetic male voice</DropdownMenuItem>
                  <DropdownMenuItem data-testid="narrate-all-adriana" onClick={() => narrateAll("adriana")}>Adriana · Roujaune coach</DropdownMenuItem>
                  <DropdownMenuItem data-testid="narrate-all-alberto" onClick={() => narrateAll("alberto")}>Alberto · Roujaune coach</DropdownMenuItem>
                  <DropdownMenuItem data-testid="narrate-all-aria" onClick={() => narrateAll("aria")}>Aria · Fit Beyond 50 companion coach</DropdownMenuItem>
                  <DropdownMenuItem data-testid="narrate-all-amelia" onClick={() => narrateAll("amelia")}>Amelia · Peaceful Pause companion</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="outline" data-testid="copy-approval-link-btn" onClick={copyLink}><Link2 size={14} className="mr-2" /> Client approval link</Button>
              <Button variant="outline" data-testid="new-coach-post-btn" onClick={() => setCoachDialogOpen(true)}><UserPlus size={14} className="mr-2" /> + {activeCompanion?.term || "Coach"} post</Button>
              {canDuet && (
                <Button variant="outline" data-testid="new-duet-post-btn" onClick={() => setDuetDialogOpen(true)}><Users size={14} className="mr-2" /> + Duet post</Button>
              )}
              <Button variant="outline" data-testid="bulk-approve-btn" disabled={batch.locked || !(posts || []).some((p) => p.review_status === "pending_review")} onClick={bulkApprove}><Check size={14} className="mr-2" /> Approve all pending</Button>
              <Button data-testid="approve-all-btn" onClick={approveAll} disabled={batch.locked}><Lock size={14} className="mr-2" /> {batch.locked ? "Schedule locked" : "Approve All & Lock Schedule"}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {batch?.status === "generating" && (
        <div className="text-center py-16 text-muted-foreground">
          <Loader2 size={28} className="mx-auto animate-spin mb-3" />
          <p>AI is writing your batch — this takes about a minute...</p>
        </div>
      )}
      {batch?.status === "failed" && <p className="text-destructive">Generation failed: {batch.error}. Try launching again.</p>}
      {!batchId && <p className="text-muted-foreground py-10 text-center">No batches yet — create one in the AI Post Generator.</p>}

      {posts.length > 0 && (
        <div className="flex items-center gap-2 text-sm flex-wrap" data-testid="motion-filter-bar">
          <span className="text-xs text-muted-foreground uppercase tracking-wider mr-1">Show:</span>
          {[
            { v: "all", l: `All (${posts.length})` },
            { v: "motion", l: `Motion (${posts.filter((p) => p.video_engine === "veo").length})` },
            { v: "slideshow", l: `Slideshow (${posts.filter((p) => (p.video_engine || "narrate") !== "veo").length})` },
          ].map(({ v, l }) => (
            <button key={v} type="button"
              data-testid={`motion-filter-${v}`}
              onClick={() => setMotionFilter(v)}
              className={`px-3 py-1 rounded-full text-xs transition-colors ${motionFilter === v ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
              {l}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <button type="button" data-testid="safe-zone-toggle" onClick={() => setShowGuides((v) => !v)}
              className={`px-3 py-1 rounded-full text-xs transition-colors border ${showGuides ? "bg-accent text-accent-foreground border-accent" : "bg-muted text-muted-foreground border-transparent hover:text-foreground"}`}
              title="Overlay Reel/TikTok safe-zone guides on each preview">
              <LayoutTemplate size={12} className="inline mr-1.5 -mt-0.5" /> Safe zones
            </button>
            {(() => {
              const visible = posts.filter((p) => motionFilter === "all" || (motionFilter === "motion" ? p.video_engine === "veo" : (p.video_engine || "narrate") !== "veo"));
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" data-testid="bulk-voice-btn" disabled={!visible.length || bulkVoiceBusy}>
                      {bulkVoiceBusy ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Mic size={12} className="mr-1.5" />} Voice for these {visible.length}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel className="text-xs">Reassign narrator voice on every visible post</DropdownMenuLabel>
                    {[
                      { v: "british", l: "British female voice" },
                      { v: "warm", l: "Warm female voice" },
                      { v: "energetic", l: "Energetic male voice" },
                      { v: "adriana", l: "Adriana · Roujaune coach" },
                      { v: "alberto", l: "Alberto · Roujaune coach" },
                      { v: "aria", l: "Aria · FB50 companion coach" },
                      { v: "amelia", l: "Amelia · Peaceful Pause companion" },
                    ].map(({ v, l }) => (
                      <DropdownMenuItem key={v} data-testid={`bulk-voice-${v}`} onClick={() => bulkReassignVoice(v, visible)}>{l}</DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })()}
          </div>
        </div>
      )}

      <DndContext sensors={dndSensors} onDragEnd={({ active, over }) => { if (over && active.id !== over.id) swapSlots(active.id, over.id); }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {posts
            .filter((p) => motionFilter === "all" || (motionFilter === "motion" ? p.video_engine === "veo" : (p.video_engine || "narrate") !== "veo"))
            .map((p) => <PostCard key={p.id} post={p} onUpdate={onUpdate} bestTime={bestTimes[p.platform]} linkClicks={clicks[p.id]} showGuides={showGuides} />)}
        </div>
      </DndContext>

      <Dialog open={coachDialogOpen} onOpenChange={setCoachDialogOpen}>
        <DialogContent className="max-w-md" data-testid="coach-post-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">New {activeCompanion?.term?.toLowerCase() || "coach"} post</DialogTitle>
            <DialogDescription>{activeCompanion ? `${activeCompanion.name} will talk followers through a topic in their own first-person voice — as a companion, not a lecturer. Video is a 45-60 s explainer narrated by them.` : "Loading companions…"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {companions.length > 1 && (
              <div>
                <Label className="text-sm mb-1.5 block">Companion</Label>
                <div className={`grid gap-2 ${companions.length >= 3 ? "grid-cols-3" : "grid-cols-2"}`}>
                  {companions.map((c) => (
                    <button key={c.key} type="button" data-testid={`coach-pick-${c.key}`}
                      onClick={() => setCoachForm({ ...coachForm, coach: c.key })}
                      className={`p-3 rounded-lg border text-left transition-colors ${coachForm.coach === c.key ? "border-accent bg-accent/10" : "border-border hover:border-accent/60"}`}>
                      <p className="font-semibold">{c.name}</p>
                      <p className="text-[11px] text-muted-foreground">{c.role}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label className="text-sm mb-1.5 block" htmlFor="coach-topic-input">Topic</Label>
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="coach-topic-presets">
                {topicPresets.map((preset) => (
                  <button key={preset} type="button" data-testid={`coach-topic-preset-${preset.replace(/ /g,'-').toLowerCase()}`}
                    onClick={() => setCoachForm({ ...coachForm, topic: preset })}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${coachForm.topic === preset ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:border-accent/60 hover:text-foreground"}`}>
                    {preset}
                  </button>
                ))}
              </div>
              <input id="coach-topic-input" type="text" data-testid="coach-topic-input"
                value={coachForm.topic}
                onChange={(e) => setCoachForm({ ...coachForm, topic: e.target.value })}
                placeholder="or write your own topic…"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent" />
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Platform</Label>
              <div className="flex gap-2">
                {["facebook", "instagram", "linkedin"].map((pl) => (
                  <button key={pl} type="button" data-testid={`coach-platform-${pl}`}
                    onClick={() => setCoachForm({ ...coachForm, platform: pl })}
                    className={`px-3 py-1.5 rounded-full text-xs capitalize transition-colors ${coachForm.platform === pl ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
                    {pl}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCoachDialogOpen(false)} data-testid="coach-dialog-cancel">Cancel</Button>
            <Button data-testid="coach-dialog-generate" onClick={createCoachPost} disabled={coachBusy || !coachForm.topic.trim() || !companions.length}>
              {coachBusy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />} Generate {activeCompanion?.term?.toLowerCase() || "coach"} post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={duetDialogOpen} onOpenChange={setDuetDialogOpen}>
        <DialogContent className="max-w-md" data-testid="duet-post-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">New duet post</DialogTitle>
            <DialogDescription>Adriana and Alberto trade tips back-and-forth on the same topic — companion-to-companion dialogue, ~55–75 s dialogue video with alternating voices and portraits.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm mb-1.5 block" htmlFor="duet-topic-input">Topic</Label>
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="duet-topic-presets">
                {["Cadence work for climbing", "Recovery week structure", "VO2 max intervals", "Race day fuelling", "Sprint form", "Fuelling long rides", "Bike fit basics"].map((preset) => (
                  <button key={preset} type="button" data-testid={`duet-topic-preset-${preset.replace(/ /g,'-').toLowerCase()}`}
                    onClick={() => setDuetForm({ ...duetForm, topic: preset })}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${duetForm.topic === preset ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:border-accent/60 hover:text-foreground"}`}>
                    {preset}
                  </button>
                ))}
              </div>
              <input id="duet-topic-input" type="text" data-testid="duet-topic-input"
                value={duetForm.topic}
                onChange={(e) => setDuetForm({ ...duetForm, topic: e.target.value })}
                placeholder="or write your own topic…"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent" />
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Platform</Label>
              <div className="flex gap-2">
                {["facebook", "instagram", "linkedin"].map((pl) => (
                  <button key={pl} type="button" data-testid={`duet-platform-${pl}`}
                    onClick={() => setDuetForm({ ...duetForm, platform: pl })}
                    className={`px-3 py-1.5 rounded-full text-xs capitalize transition-colors ${duetForm.platform === pl ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
                    {pl}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuetDialogOpen(false)} data-testid="duet-dialog-cancel">Cancel</Button>
            <Button data-testid="duet-dialog-generate" onClick={createDuetPost} disabled={duetBusy || !duetForm.topic.trim()}>
              {duetBusy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />} Generate duet post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
